'use client';

import { Container } from '@/components/layout/container/container';
import { Badge } from '@/components/ui/badge/badge';
import { MarketoForm, MarketoScript } from '@/components/ui/marketo-form';
import RichTextViewer from '@/components/ui/rich-text-viewer/rich-text-viewer';
import { cn } from '@/lib/utils';
import type { SectionBgVariant } from '@/lib/types/misc';
import type { Document } from '@contentful/rich-text-types';
import { HTMLAttributes, useEffect, useRef } from 'react';
import styles from './book-a-demo-section.module.scss';

type FeatureItem = {
  id: string;
  title: string;
  isNew?: boolean;
};

type FeatureGroup = {
  id: string;
  title: string;
  features: FeatureItem[];
};

type BookADemoSectionProps = {
  sectionId: string;
  badge?: string;
  title: string;
  description?: Document;
  layout?: '2-col' | '1-col';
  backgroundVariant?: SectionBgVariant;
  backgroundImage?: string | null;
  backgroundColorFrom?: string;
  isBackgroundDark?: boolean;
  topWidgetHtml?: string;
  bottomWidgetHtml?: string;
  featureGroups?: FeatureGroup[];
  marketoFormId?: number;
  thankYouUrl?: string;
  additionalContent?: Document;
} & HTMLAttributes<HTMLElement>;

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M14 3.7998C14.1856 3.7998 14.3638 3.87373 14.4951 4.00488C14.6264 4.13614 14.7001 4.31437 14.7002 4.5C14.7002 4.68573 14.6264 4.86379 14.4951 4.99512L6.49512 12.9951C6.43016 13.0601 6.35345 13.1122 6.26855 13.1475C6.18358 13.1827 6.09199 13.2002 6 13.2002C5.90819 13.2002 5.81725 13.1826 5.73242 13.1475C5.64744 13.1122 5.56989 13.0602 5.50488 12.9951L2.00488 9.49512C1.87373 9.36382 1.7998 9.18559 1.7998 9C1.79988 8.81438 1.87362 8.63614 2.00488 8.50488C2.13614 8.37362 2.31437 8.29988 2.5 8.2998C2.68559 8.2998 2.86382 8.37373 2.99512 8.50488L6 11.5098L13.5049 4.00488C13.6361 3.87362 13.8144 3.79988 14 3.7998Z"
      fill="#079455"
      stroke="#079455"
      strokeWidth="0.4"
    />
  </svg>
);

function EmbeddedHtmlWidget({ html }: { html: string }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current || !html) return;

    ref.current.innerHTML = '';
    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;
    ref.current.appendChild(wrapper);

    const tpWidget = ref.current.querySelector('.trustpilot-widget');
    if (tpWidget && window.Trustpilot) {
      window.Trustpilot.loadFromElement(tpWidget, true);
    }
  }, [html]);

  return <div ref={ref} suppressHydrationWarning />;
}

export const BookADemoSection = ({
  sectionId,
  badge,
  title,
  description,
  layout = '2-col',
  backgroundVariant = 'grey',
  backgroundImage,
  backgroundColorFrom,
  isBackgroundDark = false,
  topWidgetHtml,
  bottomWidgetHtml,
  featureGroups = [],
  marketoFormId = 969,
  thankYouUrl,
  additionalContent,
  className,
  ...props
}: BookADemoSectionProps) => {
  const isSingleCol = layout === '1-col';
  const isDark =
    isBackgroundDark ||
    backgroundVariant === 'purple-gradient' ||
    backgroundVariant === 'pink-gradient';
  const hasDiagonal = backgroundVariant === 'diagonal-line';

  return (
    <section
      className={cn(
        styles.component,
        styles[`component--${backgroundVariant}`],
        isDark && styles['component--dark'],
        isSingleCol && styles['component--1-col'],
        className,
      )}
      id={sectionId}
      style={
        backgroundVariant === 'custom' && backgroundColorFrom
          ? { background: backgroundColorFrom }
          : undefined
      }
      {...props}
    >
      <MarketoScript baseUrl="//info.onceforall.com" />

      {hasDiagonal && <div className={styles.diagonal_bg} aria-hidden="true" />}

      {backgroundVariant === 'image' && backgroundImage && (
        <div
          className={styles.bg_image}
          style={{ backgroundImage: `url(${backgroundImage})` }}
          aria-hidden
        />
      )}

      <Container className={styles.container}>
        <div className={cn(styles.columns, isSingleCol && styles['columns--1-col'])}>
          {/* Left column / centered header — hidden in 1-col (heading moves above form) */}
          {!isSingleCol && (
            <div className={styles.left_column}>
              {topWidgetHtml && (
                <div className={styles.top_widget}>
                  <EmbeddedHtmlWidget html={topWidgetHtml} />
                </div>
              )}

              <div className={styles.header}>
                {badge && <Badge text={badge} />}
                <h2 className={styles.title}>{title}</h2>
                {description && (
                  <div className={styles.description}>
                    <RichTextViewer content={description} dark={isDark} />
                  </div>
                )}
              </div>

              {featureGroups.length > 0 && (
                <div className={styles.feature_groups}>
                  {featureGroups.map((group) => (
                    <div key={group.id} className={styles.feature_group}>
                      <p className={styles.group_label}>{group.title}</p>
                      <ul className={styles.feature_list}>
                        {group.features.map((feature) => (
                          <li key={feature.id} className={styles.feature_item}>
                            <span className={styles.feature_icon}>
                              <CheckIcon />
                            </span>
                            <span>{feature.title}</span>
                            {feature.isNew && <span className={styles.new_badge}>New!</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Right column / centered form card */}
          <div className={cn(styles.right_column, isSingleCol && styles['right_column--1-col'])}>
            {isSingleCol && (
              <div className={styles.centered_header}>
                {topWidgetHtml && (
                  <div className={styles.trustpilot_top}>
                    <EmbeddedHtmlWidget html={topWidgetHtml} />
                  </div>
                )}
                {badge && <Badge text={badge} />}
                <h2 className={styles.title}>{title}</h2>
                {description && (
                  <div className={styles.description}>
                    <RichTextViewer content={description} dark={isDark} />
                  </div>
                )}
              </div>
            )}
            {isSingleCol && featureGroups.length > 0 && (
              <div className={styles.feature_groups}>
                {featureGroups.map((group) => (
                  <div key={group.id} className={styles.feature_group}>
                    <p className={styles.group_label}>{group.title}</p>
                    <ul className={styles.feature_list}>
                      {group.features.map((feature) => (
                        <li key={feature.id} className={styles.feature_item}>
                          <span className={styles.feature_icon}>
                            <CheckIcon />
                          </span>
                          <span>{feature.title}</span>
                          {feature.isNew && <span className={styles.new_badge}>New!</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
            <div className={styles.form_card}>
              <MarketoForm formId={marketoFormId} thankYouUrl={thankYouUrl} />
              {additionalContent && (
                <div className={styles.additional_content}>
                  <RichTextViewer content={additionalContent} dark={isDark} />
                </div>
              )}
            </div>
          </div>
        </div>
      </Container>

      {bottomWidgetHtml && (
        <Container className={styles.bottom_widget}>
          <EmbeddedHtmlWidget html={bottomWidgetHtml} />
        </Container>
      )}
    </section>
  );
};
